import type { AnchorHTMLAttributes } from "react";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";

/**
 * The one place the design system's Next.js routing assumptions are translated
 * into TanStack Router's. The design was written against `next/link` and
 * `next/navigation`; every ported component routes through the three exports
 * here instead, so the swap lives in one file rather than in each component.
 */

/**
 * An href TanStack Router must not own: another origin, another scheme
 * (`mailto:`, `tel:`) or a same-page fragment. Router `to` only understands
 * this app's own paths, and handing it one of these renders a dead link
 * rather than failing loudly.
 */
function isExternalHref(href: string) {
  return /^[a-z][a-z0-9+.-]*:/i.test(href) || href.startsWith("//") || href.startsWith("#");
}

export type UiLinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & {
  href: string;
  /** Path parameters for a templated href such as `/wallets/$applicationID`. */
  params?: Record<string, string>;
  search?: Record<string, unknown>;
};

/**
 * `next/link` with an `href` string, expressed as a TanStack `Link`. The prop
 * name stays `href` because the design's components pass it that way and M1
 * keeps the design's prop APIs; only the element underneath changes.
 *
 * `to` is deliberately fed an untyped string: this portal never declares
 * TanStack's `Register` interface (see the routes card), so link targets are
 * checked at runtime by the router, not by the compiler.
 */
export function UiLink({ href, params, search, ...rest }: UiLinkProps) {
  if (isExternalHref(href)) {
    return <a href={href} {...rest} />;
  }
  return <Link to={href} params={params} search={search} {...rest} />;
}

/**
 * `usePathname()` from `next/navigation`. Subscribing with a selector keeps a
 * nav rail from re-rendering on every search-param change, which is what a bare
 * `useRouterState()` would do.
 */
export function usePathname(): string {
  return useRouterState({ select: (state) => state.location.pathname });
}

/**
 * `useRouter().push(...)` from `next/navigation`, narrowed to the one method
 * the design's forms actually call.
 */
export function useRouterPush() {
  const navigate = useNavigate();
  return (href: string) => {
    void navigate({ to: href });
  };
}
